import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { resolveHeader } from './siteHeader';
import { resolveSiteSeo } from './siteSeo';
import { apiUrl } from './apiBase';
import { resolveHistory } from './siteHistory';
import DocumentHead from './DocumentHead';
import SiteFooter from './components/SiteFooter';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';

function InternalRafidSeedSection({ internal }) {
  return (
    <section
      className="relative border-t border-neutral-200/80 bg-gradient-to-b bg-white "
      aria-labelledby="internal-rafidseed-heading"
    >
      <div className="mx-auto max-w-shell px-4 py-16 sm:px-6 sm:py-20 md:px-8 md:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
          <div className="relative order-1 lg:order-1">
            <div
              className="pointer-events-none absolute -left-6 -top-6 h-28 w-28 rounded-full bg-brand-lime/25 blur-2xl sm:-left-10 sm:-top-10 sm:h-36 sm:w-36"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-8 -right-4 h-32 w-32 rounded-full bg-brand-forest/10 blur-2xl sm:-bottom-12 sm:right-0"
              aria-hidden
            />

            <div className="relative mx-auto max-w-lg lg:mx-0">
              <div className="rounded-[1.75rem] bg-gradient-to-br from-brand-lime/70 via-brand-lime-soft/40 to-brand-forest/25 p-[3px] shadow-[0_24px_60px_-20px_rgba(20,38,28,0.35)]">
                <div className="relative overflow-hidden rounded-[1.6rem] bg-neutral-100 ring-1 ring-black/[0.06]">
                  <div
                    className="absolute inset-0 bg-gradient-to-tr from-brand-forest/25 via-transparent to-transparent"
                    aria-hidden
                  />
                  <div
                    className="absolute left-4 top-4 z-[1] rounded-full bg-white/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-widest text-brand-forest shadow-sm ring-1 ring-black/[0.04] backdrop-blur-sm sm:left-5 sm:top-5 sm:px-3.5 sm:py-1.5 sm:text-xs"
                  >
                    Inside
                  </div>
                  <img
                    src={internal.image}
                    alt={internal.imageAlt}
                    className="aspect-[4/5] w-full scale-[1.32] object-cover object-[50%_2%] [transform-origin:50%_4%] sm:aspect-[5/6] sm:scale-[1.28] sm:object-[50%_0%] sm:[transform-origin:50%_3%] lg:min-h-[min(100vw,28rem)] lg:scale-[1.38] lg:object-[50%_0%] lg:[transform-origin:50%_2%]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-brand-forest/50 to-transparent"
                    aria-hidden
                  />
                  <div
                    className="absolute bottom-4 left-4 right-4 z-[1] rounded-xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-medium text-white shadow-lg backdrop-blur-md sm:bottom-5 sm:left-5 sm:right-5 sm:px-4 sm:text-sm"
                  >
                    {internal.caption}
                  </div>
                </div>
              </div>

              <div
                className="absolute -right-1 top-1/2 hidden h-16 w-16 -translate-y-1/2 translate-x-1/2 rounded-2xl bg-white shadow-[0_12px_30px_-8px_rgba(0,0,0,0.18)] ring-1 ring-brand-lime/40 lg:flex lg:items-center lg:justify-center"
                aria-hidden
              >
                <svg viewBox="0 0 32 32" className="h-8 w-8 text-brand-forest" fill="none" aria-hidden>
                  <path
                    d="M8 16h16M16 8v16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="opacity-40"
                  />
                  <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="1.5" className="text-brand-lime" />
                </svg>
              </div>
            </div>
          </div>

          <div className="order-2 lg:order-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.2em] text-brand-lime">{internal.eyebrow}</p>
            <h2 id="internal-rafidseed-heading" className="mt-3 text-3xl font-bold tracking-tight text-brand-forest sm:text-4xl">
              {internal.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-700 sm:text-lg">{internal.body}</p>
            <ul className="mt-8 max-w-xl space-y-4 p-0">
              {internal.bullets.map((line) => (
                <li key={line} className="flex gap-3 text-left text-[0.95rem] leading-relaxed text-neutral-800 sm:text-base">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-lime/25 text-brand-forest ring-1 ring-brand-lime/40"
                    aria-hidden
                  >
                    <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none">
                      <path
                        d="M2 6l2.5 2.5L10 3"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            
          </div>
        </div>
      </div>
    </section>
  );
}

function MilestoneCard({ title, body, image, imageAlt, accentSide }) {
  const accentAwayFromCenter = accentSide === 'right' ? 'right-0' : 'left-0';
  const imageFirst = accentSide === 'left';

  const textBlock = (
    <div className="flex min-w-0 flex-1 flex-col justify-center px-5 py-5 sm:px-6 sm:py-6">
      <h2 className="m-0 text-xl font-bold leading-snug text-neutral-900 sm:text-2xl">{title}</h2>
      <p className="mt-3 mb-0 text-sm leading-relaxed text-neutral-700 sm:text-[0.95rem]">{body}</p>
    </div>
  );

  const imageBlock = (
    <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-brand-lime-soft/50 sm:aspect-auto sm:h-auto sm:min-h-[14rem] sm:w-[min(42%,17.5rem)]">
      <div
        className={`absolute top-0 bottom-0 z-[1] w-1 bg-brand-forest ${accentAwayFromCenter}`}
        aria-hidden
      />
      <img
        src={image}
        alt={imageAlt}
        className="h-full min-h-[12rem] w-full object-cover sm:absolute sm:inset-0 sm:min-h-0 sm:h-full"
        loading="lazy"
        decoding="async"
      />
    </div>
  );

  return (
    <article className="flex max-w-2xl flex-col overflow-hidden rounded-sm border border-neutral-200/90 border-r-4 border-r-brand-lime bg-white shadow-[0_12px_40px_-18px_rgba(0,0,0,0.12)] ring-1 ring-black/[0.04] sm:flex-row sm:items-stretch">
      {imageFirst ? (
        <>
          {imageBlock}
          {textBlock}
        </>
      ) : (
        <>
          {textBlock}
          {imageBlock}
        </>
      )}
    </article>
  );
}

function HistoryTimeline({ timeline }) {
  const { eyebrow, title, intro, milestones } = timeline;
  return (
    <section className="relative" aria-labelledby="history-timeline-heading">
      <header className="relative z-20 mx-auto max-w-shell bg-white px-4 pb-10 pt-2 text-center sm:px-6 sm:pb-12 sm:pt-4 md:px-8">
        <p className="m-0 text-xs font-semibold uppercase tracking-[0.22em] text-brand-lime sm:text-sm">{eyebrow}</p>
        <h2
          id="history-timeline-heading"
          className="mt-3 text-3xl font-bold tracking-tight text-brand-forest sm:text-4xl md:text-[2.5rem] md:leading-tight"
        >
          {title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-neutral-600 sm:text-lg">{intro}</p>
      </header>

      {/* Central spine — desktop (line with round caps) */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 bottom-0 z-[0] hidden w-0 -translate-x-1/2 lg:block"
        aria-hidden
      >
        <div
          className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-lime ring-4 ring-white"
          aria-hidden
        />
        <div className="absolute left-1/2 top-2 bottom-2 w-px -translate-x-1/2 bg-neutral-300" aria-hidden />
        <div
          className="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-brand-lime ring-4 ring-white"
          aria-hidden
        />
      </div>
      {/* Mobile: vertical rail with circles at ends */}
      <div
        className="pointer-events-none absolute left-[0.65rem] top-[min(10rem,22vh)] bottom-8 z-[0] flex w-0 flex-col items-center lg:hidden"
        aria-hidden
      >
        <div className="h-2 w-2 shrink-0 rounded-full bg-brand-lime ring-4 ring-white" />
        <div className="min-h-[2rem] w-px flex-1 bg-neutral-300" />
        <div className="h-2 w-2 shrink-0 rounded-full bg-brand-lime ring-4 ring-white" />
      </div>

      <ol className="m-0 list-none p-0">
        {milestones.map((m, i) => {
          const sideRight = i % 2 === 0;
          const accentSide = sideRight ? 'right' : 'left';

          return (
            <li key={`${m.year}-${i}`} className="relative">
              <div
                className="absolute left-[0.65rem] top-[2.75rem] z-[1] h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-brand-lime ring-4 ring-white lg:hidden"
                aria-hidden
              />

              <div className="relative pl-8 sm:py-14 lg:pl-0 !pb-[0px]">
                <div className="relative mx-auto flex max-w-[1200px] flex-col gap-6 px-4 sm:px-5 lg:flex-row lg:items-center lg:gap-0">
                  {sideRight ? (
                    <>
                      <div className="relative z-20 order-2 flex flex-1 justify-center lg:order-1 lg:justify-end lg:pr-14 xl:pr-16">
                        <span className="text-2xl font-bold tabular-nums text-neutral-900 sm:text-3xl">{m.year}</span>
                      </div>
                      <div className="relative z-10 order-1 flex flex-1 justify-center lg:order-2 lg:justify-start lg:pl-8 xl:pl-12">
                        <MilestoneCard
                          title={m.title}
                          body={m.body}
                          image={m.image}
                          imageAlt={m.imageAlt}
                          accentSide={accentSide}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative z-10 order-2 flex flex-1 justify-center lg:order-1 lg:justify-end lg:pr-8 xl:pr-12">
                        <MilestoneCard
                          title={m.title}
                          body={m.body}
                          image={m.image}
                          imageAlt={m.imageAlt}
                          accentSide={accentSide}
                        />
                      </div>
                      <div className="relative z-20 order-1 flex flex-1 justify-center lg:order-2 lg:justify-start lg:pl-10 xl:pl-14">
                        <span className="text-2xl font-bold tabular-nums text-neutral-900 sm:text-3xl">{m.year}</span>
                      </div>
                    </>
                  )}

                  {/* Axis → card: anchored at centerline (first dot center = 50%) so the group does not sit under the year */}
                  <div
                    className={`pointer-events-none absolute top-1/2 z-[0] hidden -translate-y-1/2 items-center lg:flex ${
                      sideRight
                        ? 'left-[calc(50%_-_6px)] flex-row'
                        : 'right-[calc(50%_-_6px)] flex-row-reverse'
                    }`}
                    aria-hidden
                  >
                    <div className="h-3 w-3 shrink-0 rounded-full bg-brand-lime ring-4 ring-white" />
                    <div className="h-0.5 w-[min(10vw,7.5rem)] shrink-0 bg-brand-lime xl:w-[min(10vw,9rem)]" />
                    <div className="h-3 w-3 shrink-0 rounded-full bg-brand-lime ring-4 ring-white" />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default function HistoryPage() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));

  const headerSection = useMemo(() => resolveHeader(site), [site]);
  const history = useMemo(() => resolveHistory(site), [site]);
  const baseSeo = useMemo(() => resolveSiteSeo(site), [site]);

  const pageSeo = useMemo(() => {
    const brand = `${headerSection.brandPrimary} ${headerSection.brandSecondary}`.trim() || 'Rafid Seed';
    return {
      ...baseSeo,
      htmlTitle: `Our History | ${brand}`,
      socialTitle: `Our History — ${baseSeo.socialTitle || brand}`,
      canonicalUrl: typeof window !== 'undefined' ? `${window.location.origin}/history` : '',
    };
  }, [baseSeo, headerSection.brandPrimary, headerSection.brandSecondary]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <DocumentHead seo={pageSeo} />

      <MarketingSiteHeader
        site={site}
        idPrefix="history"
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <section className="relative isolate min-h-[min(26vh,240px)] overflow-hidden pt-[calc(3.85rem+env(safe-area-inset-top))] sm:min-h-[min(30vh,280px)] sm:pt-[calc(4.25rem+env(safe-area-inset-top))] md:pt-[calc(4.5rem+env(safe-area-inset-top))] pb-0">
        <div
          className={`absolute inset-0 bg-cover bg-center ${history.pageHeroImage ? '' : 'bg-neutral-300'}`}
          style={history.pageHeroImage ? { backgroundImage: `url(${history.pageHeroImage})` } : undefined}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b " aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-brand-forest/85 to-transparent" aria-hidden />
        <div className="relative z-[1] mx-auto max-w-shell px-4 mt-40">
          <div className="relative">
            <div className="relative overflow-hidden rounded-t-[60px] bg-[#e8f5e9] px-6 pb-10 pt-10 text-center shadow-[0_-20px_60px_rgba(0,0,0,0.15)]">
              <div className="absolute -left-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" aria-hidden />
              <div className="absolute -right-16 top-6 h-32 w-32 rounded-full bg-[#f3f4f6]" aria-hidden />
              <h1 className="text-3xl font-bold text-brand-forest">{history.heroTitle}</h1>
              <p className="mt-3 flex justify-center gap-2 text-xs uppercase text-brand-lime">
                <Link to="/" className="no-underline text-brand-lime hover:text-brand-forest">
                  {history.heroBreadcrumbHome}
                </Link>
                <span aria-hidden>:</span>
                <span className="text-brand-forest">{history.heroBreadcrumbCurrent}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <main className="relative bg-white pb-20">
        

        <InternalRafidSeedSection internal={history.internal} />

        <HistoryTimeline timeline={history.timeline} />

      </main>

      <SiteFooter site={site} headerSection={headerSection} />
    </div>
  );
}
